import type { ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import type { Leaves } from '@src/utils/types';
import { AgGridCurrency, AgGridSecurity, type AgGridCurrencyProps, type AgGridSecurityProps } from '../../AgGrid';
import { wrapDisplayedAssetDescriptionQuickFilter } from './assetQuickFilterText';
import { baseColumn } from './baseColumn';
import { resolveBlotterAssetIsCurrency } from './resolveBlotterAssetIsCurrency';
import { resolveSecurityColumnShowDescription } from './securityDescription';
import { wrapSecurityIdentifierQuickFilterText } from './securityIdentifiers';
import type { ColDefFactory, Column } from './types';

export interface AssetColumnProps<T extends object = any>
  extends Partial<Omit<AgGridCurrencyProps, 'showDescription'>>, Partial<AgGridSecurityProps> {
  assetTypeField?: Leaves<T>;
  /** Customize the display symbol used for searching and exports. */
  formatDisplayValue?: (displaySymbol: string, params: ValueFormatterParams<T, string>) => string;
  /** Set to true to include security identifiers in quick-filter text. This is not defaulted true to avoid secondary assets affecting the filter. */
  includeSecurityIdentifiersInQuickFilter?: boolean;
}

/**
 * Column that represents BOTH Securities and Currencies, renders both well
 *
 * - TODO - it does NOT yet support certain properties that the 'security' and 'currency' columns do, such as securityCurrencyField
 */
export const asset: ColDefFactory<Column<AssetColumnProps>> = column => {
  const showDescription = resolveSecurityColumnShowDescription(column.params);
  const displayedDescriptionQuickFilter = wrapDisplayedAssetDescriptionQuickFilter(column.getQuickFilterText, {
    ...column.params,
    showDescription,
  });

  return {
    ...baseColumn(column),
    getQuickFilterText:
      column.params?.includeSecurityIdentifiersInQuickFilter === true
        ? wrapSecurityIdentifierQuickFilterText(displayedDescriptionQuickFilter)
        : displayedDescriptionQuickFilter,
    cellRendererParams: column.params,
    cellRenderer: (params: ICellRendererParams) => {
      const assetTypeProp = column.params?.assetTypeField || '';
      const isCurrency = resolveBlotterAssetIsCurrency(params, assetTypeProp || undefined);
      if (isCurrency) {
        const showDescription =
          typeof column.params?.showDescription === 'function' ? undefined : column.params?.showDescription;
        return <AgGridCurrency {...column.params} {...params} showDescription={showDescription} />;
      }

      return <AgGridSecurity {...column.params} {...params} showDescription={showDescription} />;
    },
    valueFormatter: (params: ValueFormatterParams<unknown, string>) => {
      if (!params.value) {
        return '';
      }

      const security = params.context.current.securitiesBySymbol?.get(params.value);
      const displaySymbol = security?.DisplaySymbol ?? security?.Symbol ?? params.value;
      return column.params?.formatDisplayValue?.(displaySymbol, params) ?? displaySymbol;
    },
  };
};
